import { documentAPI } from '../api/api';

/** @typedef {'queued'|'uploading'|'done'|'error'} UploadJobStatus */

/**
 * Uygulama genelinde arka plan belge yükleme kuyruğu.
 * Sayfa değişse bile yükleme devam eder (SPA oturumu boyunca).
 */
const listeners = new Set();
/** @type {Map<string, object>} */
const jobs = new Map();
let processing = false;

const emit = () => {
  const snapshot = getJobs();
  listeners.forEach((fn) => {
    try { fn(snapshot); } catch (_) { /* ignore */ }
  });
};

export const getJobs = () => Array.from(jobs.values()).sort((a, b) => a.createdAt - b.createdAt);

export const subscribeUploads = (fn) => {
  listeners.add(fn);
  fn(getJobs());
  return () => listeners.delete(fn);
};

export const dismissUploadJob = (id) => {
  const job = jobs.get(id);
  if (!job) return;
  if (job.status === 'uploading') return;
  jobs.delete(id);
  emit();
};

export const dismissFinishedUploads = () => {
  for (const [id, job] of jobs.entries()) {
    if (job.status === 'done' || job.status === 'error') {
      jobs.delete(id);
    }
  }
  emit();
};

export const clearUploadJobError = (id) => {
  const job = jobs.get(id);
  if (!job || job.status !== 'error') return;
  job.status = 'queued';
  job.error = null;
  job.progress = 0;
  job.loaded = 0;
  emit();
  pump();
};

const formatUploadError = (err) => {
  const status = err?.response?.status;
  let msg = err?.response?.data?.message || err?.message || 'Bilinmeyen hata';
  if (status === 413) {
    msg = 'Dosya boyutu sunucu limitini aşıyor.';
  } else if (err?.code === 'ECONNABORTED' || /timeout/i.test(String(err?.message || ''))) {
    msg = 'Yükleme zaman aşımına uğradı. Tekrar denenecek / deneyin.';
  } else if (!err?.response && (err?.message === 'Network Error' || err?.code === 'ERR_NETWORK')) {
    msg = 'Ağ hatası: bağlantı koptu. Tekrar deneyin.';
  }
  return msg;
};

const pump = async () => {
  if (processing) return;
  processing = true;
  try {
    while (true) {
      const next = getJobs().find((j) => j.status === 'queued');
      if (!next) break;

      next.status = 'uploading';
      next.progress = 0;
      next.error = null;
      emit();

      try {
        await documentAPI.upload(
          next.caseFileId,
          next.file,
          next.description,
          next.documentType,
          next.faturaTarihi,
          next.tutar,
          {
            makbuz: { odenecekKdv: next.odenecekKdv ?? null, brutUcret: next.brutUcret ?? null },
            onUploadProgress: ({ progress, loaded, total, retrying, attempt }) => {
              next.progress = progress;
              next.loaded = loaded;
              next.total = total;
              next.retrying = !!retrying;
              next.attempt = attempt || 0;
              emit();
            }
          }
        );
        next.status = 'done';
        next.progress = 100;
        next.retrying = false;
        emit();
        if (typeof next.onSuccess === 'function') {
          try { next.onSuccess(next); } catch (_) { /* ignore */ }
        }
        // Başarılı işleri kısa süre sonra otomatik kaldır
        setTimeout(() => {
          if (jobs.get(next.id)?.status === 'done') {
            jobs.delete(next.id);
            emit();
          }
        }, 8000);
      } catch (err) {
        next.status = 'error';
        next.error = formatUploadError(err);
        next.retrying = false;
        emit();
        if (typeof next.onError === 'function') {
          try { next.onError(next, err); } catch (_) { /* ignore */ }
        }
      }
    }
  } finally {
    processing = false;
  }
};

/**
 * Dosyaları arka plan kuyruğuna ekler (sıralı yüklenir).
 * @returns {string[]} eklenen iş id'leri
 */
export const enqueueDocumentUploads = ({
  caseFileId,
  files,
  descriptions = {},
  documentType = 0,
  faturaTarihi = null,
  tutar = null,
  // Makbuzda her dosyanın kendi tarihi ve tutarı: { [listedekiSira]: { faturaTarihi, tutar } }
  dosyaBasina = null,
  onSuccess,
  onError,
  onBatchComplete
}) => {
  const fileArray = Array.isArray(files) ? files : [files];
  const ids = [];
  // Listedeki 1. dosya en üste gelsin diye ters sırada kuyruğa ekle
  // (backend her yeni belgeyi SortOrder=1 ile üste ekler)
  const ordered = [];
  for (let i = fileArray.length - 1; i >= 0; i--) {
    ordered.push({ file: fileArray[i], index: i });
  }

  let remaining = ordered.length;
  let successCount = 0;
  let failCount = 0;

  ordered.forEach(({ file, index }) => {
    const id = `up_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const description = (typeof descriptions === 'object' && descriptions !== null && !Array.isArray(descriptions))
      ? (descriptions[index] || '')
      : (typeof descriptions === 'string' ? descriptions : '');
    const ozel = dosyaBasina?.[index];

    jobs.set(id, {
      id,
      createdAt: Date.now(),
      caseFileId: parseInt(caseFileId, 10),
      file,
      fileName: file?.name || `dosya_${index + 1}`,
      fileSize: file?.size || 0,
      description,
      documentType,
      faturaTarihi: ozel ? ozel.faturaTarihi : faturaTarihi,
      tutar: ozel ? ozel.tutar : tutar,
      // Makbuzdan okunan ödenecek KDV ve brüt ücret
      odenecekKdv: ozel?.odenecekKdv ?? null,
      brutUcret: ozel?.brutUcret ?? null,
      status: 'queued',
      progress: 0,
      loaded: 0,
      total: file?.size || 0,
      error: null,
      retrying: false,
      attempt: 0,
      onSuccess: (job) => {
        successCount += 1;
        if (typeof onSuccess === 'function') onSuccess(job);
        remaining -= 1;
        if (remaining === 0 && typeof onBatchComplete === 'function') {
          onBatchComplete({ successCount, failCount, caseFileId: parseInt(caseFileId, 10) });
        }
      },
      onError: (job, err) => {
        failCount += 1;
        if (typeof onError === 'function') onError(job, err);
        remaining -= 1;
        if (remaining === 0 && typeof onBatchComplete === 'function') {
          onBatchComplete({ successCount, failCount, caseFileId: parseInt(caseFileId, 10) });
        }
      }
    });
    ids.push(id);
  });

  emit();
  pump();
  return ids;
};
